// EAA-1667: Moved from packages/ui/src/auth/AuthCapabilityContext.tsx into
// packages/hooks. The capability seam (can(key) contract, provider, hook) is the
// app-agnostic core an SSO/permission source wires into — future apps sharing one
// SSO backend all mount AuthCapabilityProvider and read useAuthCapability. It has
// zero UI/i18n coupling, so it belongs in the core hooks tier, not packages/ui.
import React, { createContext, useContext } from 'react';

/**
 * Single-method capability check. Consumers (packages/ui atomics, app screens)
 * cannot know *how* permissions are resolved (Redux, TanStack Query, SSO
 * claims, ...) — only that a consuming app can answer `can(key)`.
 */
export interface AuthCapability {
    can(key: string): boolean;
}

/**
 * Fail closed: with no `AuthCapabilityProvider` mounted, every gated
 * component renders as if the permission check failed. The alternative
 * (default to allow) means a team that forgets to wire the provider ships
 * every gated action fully enabled, silently — always pick safe here.
 */
const failClosedCapability: AuthCapability = {
    can: () => false,
};

const AuthCapabilityContext = createContext<AuthCapability>(failClosedCapability);
AuthCapabilityContext.displayName = 'AuthCapabilityContext';

export interface AuthCapabilityProviderProps {
    value: AuthCapability;
    children: React.ReactNode;
}

/**
 * Mounted once per app shell, wiring the app's real permission source (e.g.
 * `usePermissions()`, or SSO claims) into the `can(key)` contract
 * atomics/composites read.
 */
export const AuthCapabilityProvider: React.FC<AuthCapabilityProviderProps> = ({
    value,
    children,
}) => <AuthCapabilityContext.Provider value={value}>{children}</AuthCapabilityContext.Provider>;

export function useAuthCapability(): AuthCapability {
    return useContext(AuthCapabilityContext);
}
